import { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import useWebSocket from "react-use-websocket";
import { useDebounce } from "../../../../hooks/usedebounce";
import {
  ChartListResponse,
  ChartResponse,
  ChartTypeEnum,
  ChartUpdateRequest,
  UpdateChartPoliticalPartyRequest,
  UpdateChartRaceBallotMarksRequest,
  UpdateChartRaceRequest,
} from "../../../../models/chart";
import { ChartsService } from "../../../../services/charts-service";
import { CognitoService } from "../../../../services/cognito-service";
import { useHeight } from "./use-height";
import { useWidth } from "./use-width";

export type BallotMarkupModelsState = {
  loading: boolean;
  saving: boolean;
  pageNumber: number;
  totalPages: number;
  pdfUrl: string;
  chart?: ChartResponse;
  currentMarks: UpdateChartRaceBallotMarksRequest[];
  wrapperDivRef: React.MutableRefObject<null>;
  width: number | null;
  totalMarksNeeded: number;
  showResetDialog: boolean;
  dirty: boolean;
  generating: boolean;
  showChartSearchDialog: boolean;
  chartSearchQuery: string;
  chartSearchYear: string;
  chartSearchResults: ChartListResponse[];
  showImportStatusModal: boolean;
  importSuccessful: boolean;
  importMessage: string;
  markupOverwrite: boolean;
};

export type BallotMarkupFunctionState = {
  setChartId: (chartId: string) => void;
  setPageNumber: (pageNumber: number) => void;
  onDocumentLoadSuccess: (e: any) => any;
  updateClick: (newX: number, newY: number, pageNum: number) => void;
  saveMarks: () => void;
  convertXToDBSize: (value: number) => number;
  convertXFromDBSize: (value: number) => number;
  convertYToDBSize: (value: number) => number;
  convertYFromDBSize: (value: number) => number;
  undoMark: () => void;
  updateCurrentPage: (newPage: number) => void;
  clearMarks: () => void;
  startClearMarks: () => void;
  cancelClearMarks: () => void;
  openChartSearch: () => void;
  cancelChartSearch: () => void;
  updateChartSearchQuery: (query: string) => void;
  updateChartSearchYear: (year: string) => void;
  searchCharts: () => void;
  importMarks: () => void;
  selectedChart: ChartResponse;
  updateSelectedChart: (chart: ChartResponse) => void;
  generateTestDeck: () => void;
  toggleImportStatusModal: () => void;
  toggleMarkupOverwrite: () => void;
  overwriteExistingMarks: () => void;
};

export type BallotMarkupState = {
  models: BallotMarkupModelsState;
  functions: BallotMarkupFunctionState;
};

export const useBallotMarkup = (): BallotMarkupState => {
  const navigate = useNavigate();
  const wrapperDivRef = useRef(null);
  const width = useWidth(wrapperDivRef);
  const height = useHeight(wrapperDivRef);
  const columnMargin = 3000; // Super big since we're dealing with the scaled up values
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showResetDialog, setShowResetDialog] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalMarksNeeded, setTotalMarksNeeded] = useState<number>(0);
  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [chartId, setChartId] = useState<string>("");
  const [chart, setChart] = useState<ChartResponse>({} as ChartResponse);
  const [currentMarks, setCurrentMarks] = useState<
    UpdateChartRaceBallotMarksRequest[]
  >([] as UpdateChartRaceBallotMarksRequest[]);
  const [firstPageColumns, setFirstPageColumns] = useState<number[]>([]);
  const [secondPageColumns, setSecondPageColumns] = useState<number[]>([]);
  const params = useParams<{ chartId: string }>();
  const [dirty, setDirty] = useState<boolean>(false);
  const [showChartSearchDialog, setShowChartSearchDialog] =
    useState<boolean>(false);
  const [chartSearchResults, setChartSearchResults] = useState<
    ChartListResponse[]
  >([] as ChartListResponse[]);
  const [chartSearchQuery, setChartSearchQuery] = useState<string>("");
  const [chartSearchYear, setChartSearchYear] = useState<string>("");
  const [selectedChart, setSelectedChart] = useState<ChartResponse>(
    {} as ChartResponse
  );
  const debouncedQuery = useDebounce(chartSearchQuery, 800);
  const debouncedYear = useDebounce(chartSearchYear, 800);
  const [generating, setGenerating] = useState<boolean>(false);
  const [shouldWebSocketConnect, setShouldWebSocketConnect] =
    useState<boolean>(false);
  const [token, setToken] = useState<string>("");
  const [showImportStatusModal, setShowImportStatusModal] =
    useState<boolean>(false);
  const [importSuccessful, setImportSuccessful] = useState<boolean>(false);
  const [importMessage, setImportMessage] = useState<string>("");
  const [markupOverwrite, setMarkupOverwrite] = useState<boolean>(false);

  const getChartDetails = useCallback(async (chartId: string) => {
    try {
      setLoading(true);
      const currentChart = await ChartsService.getChartDetailsForMarks(chartId);
      setChart(currentChart);

      console.log(currentChart)
      // get how many marks are needed
      setTotalMarksNeeded(getMarksAvailableForPage(currentChart));
      // pre-populate any existing marks
      loadExistingMarks(currentChart);

      const url = await ChartsService.getBallotFileS3Link(chartId);
      setPdfUrl(url);
      if (window) {
        window.scrollTo(0, 0);
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMarksAvailableForPage = (chart: ChartResponse): number => {
    let needed = 0;
    chart.races.forEach((race) => {
      let raceNeeds = race.writeIns;
      if (race.politicalParties && race.politicalParties.length > 0) {
        if (chart.electionType.id === ChartTypeEnum.PARTISAN_PRIMARY && race.name !== 'Preferred Party') {
          // partisan primary has the data stored differently than other races
          raceNeeds += race.candidates;
        } else {
          raceNeeds += race.politicalParties
            .map((c) => c.candidates)
            .reduce((ca, cb) => ca + cb, 0);
        }
      } else {
        raceNeeds += race.candidates;
        if (race.type.toLowerCase() === "proposal") {
          raceNeeds += 2;
        }
      }
      needed += raceNeeds;
    });
    return needed;
  };

  const loadExistingMarks = (chart: ChartResponse) => {
    const existingMarks: UpdateChartRaceBallotMarksRequest[] = [];
    const sortedRaces = chart.races.sort((ra, rb) =>
      ra.order < rb.order ? 1 : 0
    );

    sortedRaces.forEach((race) => {
      if (race.ballotMarks && race.ballotMarks.length > 0) {
        const sortedMarks = race.ballotMarks.sort((ra, rb) =>
          ra.order < rb.order ? 1 : 0
        );
        sortedMarks.forEach((mark) => {
          const newMark: UpdateChartRaceBallotMarksRequest = {
            id: "",
            isOnSecondPage: mark.isOnSecondPage,
            order: existingMarks.length,
            x: mark.x,
            y: mark.y,
          };
          getSnappedXValue(mark.x, mark.isOnSecondPage ? 2 : 1); // make sure we update our "columns" for any new marks
          existingMarks.push(newMark);
        });
      }
    });
    setCurrentMarks(existingMarks);
  };

  const onDocumentLoadSuccess = ({ numPages }: { numPages: any }) => {
    setTotalPages(numPages);
  };

  useEffect(() => {
    if (params.chartId && params.chartId !== chartId) {
      setChartId(params.chartId);
      getChartDetails(params.chartId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, getChartDetails]);

  const getSnappedXValue = (x: number, pageNum: number): number => {
    if (pageNum === 1) {
      if (
        firstPageColumns.length === 0 ||
        firstPageColumns.filter(
          (c) => c - columnMargin <= x && c + columnMargin >= x
        ).length === 0
      ) {
        setFirstPageColumns((prevState) => {
          if (![...prevState].includes(x)) {
            return [...prevState, x];
          } else {
            return [...prevState];
          }
        });
        return x;
      } else {
        return firstPageColumns.filter(
          (c) => c - columnMargin <= x && c + columnMargin >= x
        )[0];
      }
    } else {
      if (
        secondPageColumns.length === 0 ||
        secondPageColumns.filter(
          (c) => c - columnMargin <= x && c + columnMargin >= x
        ).length === 0
      ) {
        setSecondPageColumns((prevState) => {
          if (![...prevState].includes(x)) {
            return [...prevState, x];
          } else {
            return [...prevState];
          }
        });

        return x;
      } else {
        return secondPageColumns.filter(
          (c) => c - columnMargin <= x && c + columnMargin >= x
        )[0];
      }
    }
  };

  const updateClick = async (newX: number, newY: number, pageNum: number) => {
    if (currentMarks.length + 1 > totalMarksNeeded) {
      return;
    }
    setDirty(true);
    if (newX === 0 || newY === 0) {
      console.log("got zeros");
    }
    const x = convertXToDBSize(newX);
    const y = convertYToDBSize(newY);
    const snappedX = getSnappedXValue(x, pageNum);

    // format the mark
    const newMark: UpdateChartRaceBallotMarksRequest = {
      id: "",
      isOnSecondPage: pageNum === 2,
      order: currentMarks.length + 1,
      x: snappedX,
      y: y,
    };
    setCurrentMarks([...currentMarks, newMark]);

    // TODO: possible future work, after we've clicked, see what race we're working with
    // if this is the second mark of that race, auto populate the rest of the marks for
    // the race with the same x value, and new y values based on the difference between
    // this mark and the first mark of the race. Now sure how well this would work,
    // but should be possible an would end up saving the user clicks
  };

  const updateCurrentPage = (newPageNumber: number) => {
    setPageNumber(newPageNumber);
  };

  const undoMark = () => {
    setDirty(true);
    const lastMark = currentMarks[currentMarks.length - 1];
    const newMarks = [...currentMarks.slice(0, -1)];
    setCurrentMarks([...currentMarks.slice(0, -1)]);
    if (
      newMarks.filter(
        (x) =>
          x.x === lastMark.x && x.isOnSecondPage === lastMark.isOnSecondPage
      ).length === 0
    ) {
      if (lastMark.isOnSecondPage) {
        setSecondPageColumns(secondPageColumns.filter((x) => x !== lastMark.x));
      } else {
        setFirstPageColumns(firstPageColumns.filter((x) => x !== lastMark.x));
      }
    }
  };

  const clearMarks = () => {
    setDirty(true);
    setShowResetDialog(false);
    setCurrentMarks([]);
    setFirstPageColumns([]);
    setSecondPageColumns([]);
  };

  const startClearMarks = () => {
    setShowResetDialog(true);
  };

  const cancelClearMarks = () => {
    setShowResetDialog(false);
  };

  const saveMarks = async () => {
    try {
      setSaving(true);
      // assign marks to races
      const markedRaces = assignMarksToRaces();

      const markedChart: ChartUpdateRequest = {
        adminchart: chart.adminChart,
        county: chart.county,
        electionTypeId: chart.electionType.id,
        electiondate: chart.electionDate,
        showCandidateNames: chart.showCandidateNames,
        id: chart.id,
        labelsonright: chart.labelsOnRight,
        municipality: chart.municipality,
        politicalParties: chart.politicalParties as UpdateChartPoliticalPartyRequest[],
        precinct: chart.precinct,
        races: markedRaces,
        stateId: chart.state.id,
        status: chart.status,
      };
      // send update to db
      await ChartsService.updateChartManualMarks(markedChart);
      setDirty(false);
    } catch (e: any) {
      console.log(e);
      toast.error(e?.response?.data?.message || "Error saving marks");
    } finally {
      setSaving(false);
    }
  };

  const generateTestDeck = async () => {
    try {
      setGenerating(true);
      await saveMarks();
      const session = await CognitoService.getSession();
      if (session) {
        setToken(session.getIdToken().getJwtToken());
      }
      setShouldWebSocketConnect(true);
      // generate test deck
    } catch (e: any) {
      console.log(e);
      toast.error(e?.response?.data?.message || "Error generating test deck");
      setGenerating(false);
    }
  };

  const getWebsocketUrl = useCallback(() => {
    return `${process.env.REACT_APP_SERVICE_URL?.replace(
      "http://",
      "ws://"
    ).replace("https://", "wss://")}/charts/generate-full-test-deck/${chartId}`;
  }, [chartId]);

  // returns the URL to send the user to based on the charts' electionType
  const getUrl = (): string => {
    if (chart.electionType.id === ChartTypeEnum.NON_PARTISAN) {
      return "/elections/non-partisan?status=Completed";
    } else if (chart.electionType.id === ChartTypeEnum.PARTISAN_PRIMARY) {
      return "/elections/partisan?status=Completed";
    } else if (chart.electionType.id === ChartTypeEnum.PRESIDENTIAL_PRIMARY) {
      return "/elections/presidential?status=Completed";
    } else {
      return "/elections/general?status=Completed";
    }
  };

  useWebSocket(
    getWebsocketUrl,
    {
      share: false,
      shouldReconnect: (closeEvent) => false,
      onClose: (event: WebSocketEventMap["close"]) => {
        setSaving(false);
        setToken("");
        return;
      },
      onMessage: (event: WebSocketEventMap["message"]) => {
        console.log("message", event.data);
        if (event.data.includes("FINISHED")) {
          setShouldWebSocketConnect(false);
          setToken("");
          navigate(getUrl());
        } else if (event.data.includes("ERROR")) {
          // set should connect to false
          setShouldWebSocketConnect(false);
          toast.error("Error occurred while generating the chart files.");
          setToken("");
          setSaving(false);
          setGenerating(false);
        }
      },
      onError: (event: WebSocketEventMap["error"]) => {
        setShouldWebSocketConnect(false);
        toast.error("Error occurred while generating the chart files.");
        setToken("");
        setSaving(false);
        setGenerating(false);
      },
      protocols: ["Authorization", token],
    },
    shouldWebSocketConnect
  );

  const assignMarksToRaces = (): UpdateChartRaceRequest[] => {
    let lastIndex = 0;
    const sortedMarks = currentMarks.sort((ra, rb) =>
      ra.order < rb.order ? 1 : 0
    );
    const markedRaces: UpdateChartRaceRequest[] = [];
    const sortedRaces = chart!.races.sort((ra, rb) =>
      ra.order < rb.order ? 1 : 0
    );
    for (const race of sortedRaces) {
      let totalMarks = 0;
      // calculate total number of possible marks per race
      if (race.politicalParties && race.politicalParties.length > 0) {
        if (chart.electionType.id === ChartTypeEnum.PARTISAN_PRIMARY && !race.isStraightParty) {
          // partisan primary stores the number of candidates per party on the race not in the parties
          totalMarks = race.candidates + race.writeIns;
        } else {
          totalMarks =
            race.politicalParties
              .map((party) => party.candidates)
              .reduce((prev, curr) => prev + curr, 0) + race.writeIns;
        }
      } else {
        totalMarks = race.candidates + race.writeIns;
        if (race.type.toLowerCase() === "proposal") {
          totalMarks = 2;
        }
      }

      const raceMarks =
        race.isStraightParty && chart.electionType.id === ChartTypeEnum.PARTISAN_PRIMARY
          ? sortedMarks.slice(0, totalMarks)
          : lastIndex === 0
            ? sortedMarks.slice(lastIndex, totalMarks)
            : sortedMarks.slice(lastIndex, lastIndex + totalMarks);

      // update the order of the marks for the current race
      new Array(totalMarks).forEach((_, index) => {
        raceMarks[index].order = index;
      });
      (race as unknown as UpdateChartRaceRequest).ballotMarks = raceMarks;
      markedRaces.push(race as unknown as UpdateChartRaceRequest);
      lastIndex = lastIndex + totalMarks;
    }
    return markedRaces;
  };

  const convertXToDBSize = (value: number): number => {
    if (width) {
      const scaledXValue = (value / width) * chart.ballotFile.width;
      return Math.floor(scaledXValue);
    } else {
      // should ever happen, but just in case
      return value;
    }
  };

  const convertXFromDBSize = (value: number): number => {
    if (width) {
      const scaledXValue = (value / chart.ballotFile.width) * width;
      return Math.floor(scaledXValue);
    } else {
      // should ever happen, but just in case
      return value;
    }
  };

  const convertYToDBSize = (value: number): number => {
    if (height) {
      let h = height;
      if (chart.ballotFile.isTwoPages) {
        h = h / 2;
      }
      const scaledYValue = (value / h) * chart.ballotFile.height;
      return Math.floor(scaledYValue);
    } else {
      // should ever happen, but just in case
      return value;
    }
  };

  const convertYFromDBSize = (value: number): number => {
    if (height) {
      let h = height;
      if (chart.ballotFile.isTwoPages) {
        h = h / 2;
      }
      const scaledYValue = (value / chart.ballotFile.height) * h;
      return Math.floor(scaledYValue);
    } else {
      // should ever happen, but just in case
      return value;
    }
  };

  const openChartSearch = () => {
    setShowChartSearchDialog(true);
  };

  const cancelChartSearch = () => {
    setShowChartSearchDialog(false);
  };

  const updateChartSearchQuery = (query: string) => {
    setChartSearchQuery(query);
  };

  const searchCharts = useCallback(async () => {
    try {
      let charts: ChartListResponse[] = [];
      if (chartSearchQuery && !chartSearchYear) {
        charts = await ChartsService.searchCharts(chart.id, chartSearchQuery);
      } else {
        charts = await ChartsService.searchCharts(
          chart.id,
          chartSearchQuery,
          chartSearchYear
        );
      }

      setChartSearchResults(charts);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Error searching charts");
    }
  }, [chart, chartSearchQuery, chartSearchYear]);

  const updateChartSearchYear = (year: string) => {
    setChartSearchYear(year);
  };

  const importMarks = async () => {
    if (currentMarks.length > 0 && !markupOverwrite) {
      setMarkupOverwrite(true);
    } else {
      try {
        const importedChartRaces = await ChartsService.importMarksFromChart(
          `${chartId}`,
          selectedChart!.id
        );
        if (importedChartRaces.length > 0) {
          const existingMarks: UpdateChartRaceBallotMarksRequest[] = [];
          const sortedRaces = importedChartRaces.sort((ra, rb) =>
            ra.order < rb.order ? 1 : 0
          );

          sortedRaces.forEach((race) => {
            if (race.ballotMarks && race.ballotMarks.length > 0) {
              const sortedMarks = race.ballotMarks.sort((ra, rb) =>
                ra.order < rb.order ? 1 : 0
              );
              sortedMarks.forEach((mark) => {
                const newMark: UpdateChartRaceBallotMarksRequest = {
                  id: "",
                  isOnSecondPage: mark.isOnSecondPage,
                  order: existingMarks.length,
                  x: mark.x,
                  y: mark.y,
                };
                getSnappedXValue(mark.x, mark.isOnSecondPage ? 2 : 1); // make sure we update our "columns" for any new marks
                existingMarks.push(newMark);
              });
            }
          });
          setCurrentMarks(existingMarks);
          setImportMessage(
            "Marks have successfully been imported to a certain point. Please continue filling out the remaining marks."
          );
          setImportSuccessful(true);
        } else {
          setImportSuccessful(false);
          setImportMessage(
            "We are unable to find any matching races with data to import."
          );
        }
        setShowImportStatusModal(true);
      } catch (e: any) {
        setImportSuccessful(false);
        setImportMessage("An error occurred while importing marks.");
        setShowImportStatusModal(true);
      }
    }
  };

  const updateSelectedChart = (selectedChart: ChartResponse) => {
    setSelectedChart(selectedChart);
  };

  useEffect(() => {
    if (debouncedQuery) {
      searchCharts();
    }
  }, [debouncedQuery, debouncedYear, searchCharts]);

  useEffect(() => {
    if (!showChartSearchDialog) {
      setChartSearchQuery("");
      setChartSearchYear("");
      setChartSearchResults([]);
    } else {
      searchCharts();
    }
  }, [
    showChartSearchDialog,
    setChartSearchQuery,
    setChartSearchResults,
    searchCharts,
  ]);

  // closes the import status modal and the chart search modal
  const toggleImportStatusModal = () => {
    if (importSuccessful) {
      cancelChartSearch();
    }
    setShowImportStatusModal(!showImportStatusModal);
  };

  const toggleMarkupOverwrite = () => {
    setMarkupOverwrite(!markupOverwrite);
  };

  const overwriteExistingMarks = async () => {
    await importMarks();
    setMarkupOverwrite(false);
  };

  return {
    models: {
      loading,
      chart,
      pdfUrl,
      pageNumber,
      currentMarks,
      totalPages,
      wrapperDivRef,
      width,
      saving,
      totalMarksNeeded,
      showResetDialog,
      showChartSearchDialog,
      chartSearchResults,
      chartSearchQuery,
      chartSearchYear,
      dirty,
      generating,
      importSuccessful,
      showImportStatusModal,
      importMessage,
      markupOverwrite,
    },

    functions: {
      setChartId,
      setPageNumber,
      saveMarks,
      onDocumentLoadSuccess,
      updateClick,
      convertXToDBSize,
      convertXFromDBSize,
      convertYToDBSize,
      convertYFromDBSize,
      undoMark,
      updateCurrentPage,
      clearMarks,
      startClearMarks,
      cancelClearMarks,
      openChartSearch,
      cancelChartSearch,
      updateChartSearchQuery,
      updateChartSearchYear,
      searchCharts,
      importMarks,
      selectedChart,
      updateSelectedChart,
      generateTestDeck,
      toggleImportStatusModal,
      toggleMarkupOverwrite,
      overwriteExistingMarks,
    },
  };
};
